import React from 'react';
import { ShoppingCartIcon, MinusIcon, PlusIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { useStore } from '../../lib/store';
import { haptics } from '../../lib/utils/haptics';
import { toast } from 'sonner';
interface AddToCartProps {
  product: {
    id: string | number;
    name: string;
    price: number;
    images: string[];
    brand?: string;
  };
}
export function AddToCart({
  product
}: AddToCartProps) {
  const productId = typeof product.id === 'string' ? parseInt(product.id) : product.id;
  const {
    cart,
    addToCart,
    updateQuantity
  } = useStore();
  const cartItem = cart.find(item => item.id === productId);
  const quantity = cartItem?.quantity || 0;
  const handleAddToCart = () => {
    haptics.success();
    addToCart({
      id: productId,
      name: product.name,
      price: product.price,
      image: product.images[0],
      brand: product.brand
    });
    toast.success(`${product.name} added to cart!`);
  };
  const handleIncrement = () => {
    haptics.light();
    updateQuantity(productId, quantity + 1);
  };
  const handleDecrement = () => {
    haptics.light();
    if (quantity > 0) {
      updateQuantity(productId, quantity - 1);
    }
  };
  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    updateQuantity(productId, isNaN(value) || value < 0 ? 0 : value);
  };
  return <div className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden" data-id="element-5333" data-mp="77drlb83do8t">
      {/* Content */}
      <div className="p-5" data-id="element-5334" data-mp="c79vlagshfod">
        {/* Quantity Selector */}
        <div className="mb-4" data-id="element-5335" data-mp="c4nk4zqs2g0v">
          <label className="block text-sm font-bold text-gray-900 mb-2" data-id="element-5336" data-mp="3tfwtbsa4o0lx">
            Quantity
          </label>
          <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden w-max" data-id="element-5337" data-mp="27sqov9d35tg">
            <button onClick={handleDecrement} className="px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700 font-bold" data-id="element-5338" data-mp="2hz6sypsdy308">
              −
            </button>
            <input type="number" value={quantity} onChange={handleQuantityChange} min="1" className="w-16 text-center border-x-2 border-gray-200 py-3 focus:outline-none font-medium" data-id="element-5339" data-mp="tf11v9ugf1bs" />
            <button onClick={handleIncrement} className="px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700 font-bold" data-id="element-5340" data-mp="ery3z227yqnx">
              +
            </button>
          </div>
        </div>
        {/* Add to Cart Button */}
        <motion.button whileTap={{
        scale: 0.98
      }} onClick={handleAddToCart} className="mb-3 flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-4 font-bold text-white shadow-md transition-[background-color,box-shadow,transform] duration-200 hover:bg-brand-dark hover:shadow-lg" data-id="element-5341" data-mp="5t47rcssw2aw">
          <motion.div key={quantity > 0 ? 'added' : 'add'} initial={{
          scale: 0.8,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="flex items-center gap-2" data-id="element-5342" data-mp="1q7wezbgyjmrs">
            <ShoppingCartIcon className="w-5 h-5" data-id="element-5343" data-mp="27gfi0ozyhc72" />
            {quantity > 0 ? 'Added to Cart' : 'Add to Cart'}
          </motion.div>
        </motion.button>
        {/* Apple Pay Button */}
        <button className="w-full bg-black hover:bg-gray-900 text-white font-bold py-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-md hover:shadow-lg" data-id="element-5344" data-mp="1esjlpleatmat">
          <svg className="w-12 h-5" viewBox="0 0 48 20" fill="none" data-id="element-5345" data-mp="1oompswspnv0e">
            <path d="M9.4 4.2c.6-.7 1-1.7.9-2.7-.9 0-2 .6-2.6 1.3-.6.6-1.1 1.6-1 2.6 1 .1 2.1-.5 2.7-1.2zm.9 1.4c-1.5-.1-2.8.8-3.5.8s-1.8-.8-3-.8c-1.5 0-2.9.9-3.7 2.2-1.6 2.7-.4 6.7 1.1 8.9.7 1.1 1.6 2.3 2.8 2.2 1.1 0 1.6-.7 3-.7 1.3 0 1.7.7 3 .7 1.2 0 2-1 2.7-2.1.8-1.2 1.2-2.4 1.2-2.5 0 0-2.3-.9-2.4-3.5 0-2.2 1.8-3.2 1.9-3.3-1-1.5-2.6-1.7-3.1-1.9z" fill="currentColor" data-id="element-5346" data-mp="1tp22dm564yhb" />
            <path d="M24.8 3.8c2.1 0 3.5 1.4 3.6 3.5h-2c-.1-1.2-.9-1.9-2-1.9-1.1 0-1.9.7-1.9 1.7 0 .8.6 1.3 1.9 1.6l1.3.3c2 .5 2.9 1.3 2.9 2.8 0 1.9-1.5 3.1-3.8 3.1-2.2 0-3.6-1.3-3.7-3.4h2.1c.1 1.2 1 2 2 2 1.1 0 1.9-.7 1.9-1.7 0-.8-.6-1.3-1.9-1.6l-1.2-.3c-2-.5-2.9-1.4-2.9-2.9 0-1.8 1.5-3.2 3.7-3.2zm5.8.2h2v1.6h.1c.3-1.1 1.3-1.8 2.5-1.8.3 0 .6 0 .8.1v2c-.3-.1-.7-.2-1-.2-1.3 0-2.2.9-2.2 2.2v6h-2.2V4zm7.7 10.2c0-1.7 1.3-2.7 3.6-2.8l2.6-.2v-.6c0-1-.7-1.6-1.8-1.6-1 0-1.7.5-1.9 1.3h-2c.1-1.8 1.6-3.1 3.9-3.1 2.4 0 3.9 1.3 3.9 3.3v6.9h-2v-1.5h-.1c-.5 1-1.6 1.7-2.9 1.7-1.8 0-3.3-1.1-3.3-2.8v-.6zm6.2-.7v-.6l-2.3.2c-1.2.1-1.8.6-1.8 1.3 0 .7.6 1.2 1.6 1.2 1.2 0 2.5-.7 2.5-2.1z" fill="currentColor" data-id="element-5347" data-mp="2yo77mabhpdyo" />
          </svg>
          <span data-id="element-5348" data-mp="943wzts7pj0s">Pay</span>
        </button>
      </div>
    </div>;
}